import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import http from "@/lib/http";
import { Moon, RotateCcw, Trash2, Search, AlertTriangle } from "lucide-react";
import LiveStatusBadge from "@/components/LiveStatusBadge";

const REASONS = [
  "Geen interesse", "Verkocht", "Onbekend adres", "Dubbel pand", "Andere",
];

export default function Archive() {
  const [data, setData] = useState({ items: [], users: {} });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null); // lead object

  const load = async () => {
    try {
      setLoading(true);
      const r = await http.get("/api/archive/leads");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    setBusy(id);
    try { await http.post(`/api/leads/${id}/restore`); await load(); } finally { setBusy(null); }
  };

  const confirmHardDelete = async () => {
    if (!hardDeleteTarget) return;
    const id = hardDeleteTarget.id;
    setBusy(id);
    try {
      await http.delete(`/api/leads/${id}?hard=true`);
      setHardDeleteTarget(null);
      await load();
    } finally {
      setBusy(null);
    }
  };

  const filtered = (() => {
    if (!query.trim()) return data.items;
    const q = query.toLowerCase();
    return data.items.filter(L =>
      (L.address || "").toLowerCase().includes(q) ||
      (L.postal_code || "").toLowerCase().includes(q) ||
      (L.city || "").toLowerCase().includes(q) ||
      (L.archive_reason || "").toLowerCase().includes(q)
    );
  })();

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="archive-page">
      <div className="flex items-center gap-3 mb-1">
        <Moon size={22} className="text-[var(--teal-700)]" />
        <h1 className="font-display text-[28px] text-[var(--ink-900)]">Op rust</h1>
        <span className="ml-2 chip !text-[11px] tabular bg-[var(--sand-100)] text-[var(--ink-700)] border-[var(--border-soft)]"
              data-testid="archive-count">
          {data.items.length} {data.items.length === 1 ? "pand" : "panden"}
        </span>
      </div>
      <p className="text-[13px] text-[var(--ink-500)] mb-6 max-w-2xl">
        Panden die je op rust hebt gezet — al je werk (status, notities, eigenaars, prospectie) blijft bewaard.
        Komt het pand opnieuw online in Supabase, dan plaatst FlowNavigator de lead automatisch terug in
        de juiste pipeline. Klik op <em>Herstellen</em> om dat manueel te doen.
      </p>

      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek op adres, postcode, reden…"
          data-testid="archive-search"
          className="w-full h-10 pl-9 pr-3 rounded-md border border-[var(--border-strong)] text-[13px] focus:outline-none focus:border-[var(--teal-500)]"
        />
      </div>

      {loading ? (
        <div className="text-[13px] text-[var(--ink-500)]">Bezig met laden…</div>
      ) : filtered.length === 0 ? (
        <div className="card !p-8 text-center" data-testid="archive-empty">
          <Moon size={32} className="mx-auto text-[var(--ink-300)] mb-3" />
          <div className="font-display text-[18px] text-[var(--ink-900)] mb-1">
            {data.items.length === 0 ? "Geen panden op rust" : "Geen resultaten"}
          </div>
          <div className="text-[13px] text-[var(--ink-500)]">
            {data.items.length === 0
              ? "Zet je een lead op rust, dan komt die hier terecht."
              : "Probeer een andere zoekterm."}
          </div>
        </div>
      ) : (
        <div className="card !p-0 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[var(--sand-50)] text-[var(--ink-500)] uppercase tracking-wider text-[10px]">
              <tr>
                <th className="px-3 py-3 w-[68px]"></th>
                <th className="text-left px-3 py-3">Adres</th>
                <th className="text-left px-3 py-3">Postcode</th>
                <th className="text-left px-3 py-3">Type</th>
                <th className="text-left px-3 py-3">Online</th>
                <th className="text-left px-3 py-3">Reden</th>
                <th className="text-left px-3 py-3">Door</th>
                <th className="text-left px-3 py-3">Wanneer</th>
                <th className="text-right px-4 py-3">Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(L => {
                const actor = data.users?.[L.archived_by]?.name || "—";
                const photo = (L.photos && L.photos[0]) || null;
                return (
                  <tr key={L.id}
                      data-testid={`archive-row-${L.id}`}
                      className="border-t border-[var(--border-soft)] hover:bg-[var(--sand-50)]/40">
                    <td className="px-3 py-2">
                      <Link to={`/lead/${L.id}`}
                            className="block w-12 h-12 rounded-md overflow-hidden bg-[var(--sand-100)] border border-[var(--border-soft)] flex items-center justify-center">
                        {photo ? (
                          <img src={photo} alt={L.address || ""} className="w-full h-full object-cover"
                               onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        ) : (
                          <Moon size={16} className="text-[var(--ink-300)]" />
                        )}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5">
                      <Link to={`/lead/${L.id}`} className="font-semibold text-[var(--ink-900)] hover:text-[var(--teal-700)]">
                        {L.address || "—"}
                      </Link>
                      <div className="text-[11px] text-[var(--ink-500)]">{L.city}</div>
                    </td>
                    <td className="px-3 py-2.5 tabular text-[var(--ink-700)]">{L.postal_code}</td>
                    <td className="px-3 py-2.5 capitalize text-[var(--ink-700)]">{L.source_type}</td>
                    <td className="px-3 py-2.5">
                      <LiveStatusBadge lead={L} />
                    </td>
                    <td className="px-3 py-2.5 text-[var(--ink-700)]">
                      {L.archive_reason || <span className="text-[var(--ink-400)] italic">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-[var(--ink-700)]">{actor}</td>
                    <td className="px-3 py-2.5 text-[var(--ink-500)] tabular">
                      {L.archived_at ? new Date(L.archived_at).toLocaleString("nl-BE", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      }) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button type="button"
                              disabled={busy === L.id}
                              onClick={() => restore(L.id)}
                              data-testid={`archive-restore-${L.id}`}
                              className="btn btn-ghost !h-8 !px-2.5 !text-[12px] disabled:opacity-50">
                        <RotateCcw size={12} /> {busy === L.id ? "Bezig…" : "Herstellen"}
                      </button>
                      <button type="button"
                              disabled={busy === L.id}
                              onClick={() => setHardDeleteTarget(L)}
                              data-testid={`archive-hard-delete-${L.id}`}
                              title="Definitief wissen — enkel voor testdata of duplicaten"
                              className="inline-flex items-center gap-1 h-8 px-2.5 ml-1 text-[12px] font-semibold
                                         rounded-md border-2 border-[#B43232] bg-white text-[#B43232]
                                         hover:bg-[#FBE7E7] disabled:opacity-50">
                        <AlertTriangle size={12} /> Definitief wissen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hardDeleteTarget && (
        <HardDeleteDialog
          target={hardDeleteTarget}
          busy={busy === hardDeleteTarget.id}
          onCancel={() => setHardDeleteTarget(null)}
          onConfirm={confirmHardDelete}
        />
      )}
    </div>
  );
}

function HardDeleteDialog({ target, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         data-testid="hard-delete-modal"
         onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 border-t-4 border-[#B43232]">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={20} className="text-[#B43232]" />
          <h3 className="font-display text-[20px] text-[#B43232]">Definitief wissen</h3>
        </div>
        <p className="text-[13px] text-[var(--ink-700)] mb-2">
          Je staat op het punt om <strong>&ldquo;{target.address || "deze lead"}&rdquo;</strong> definitief te wissen.
        </p>
        <p className="text-[13px] text-[var(--ink-700)] mb-4 p-3 bg-[#FBE7E7] border border-[#EFBABA] rounded-md">
          Dit wist het pand en alle historie definitief. Gebruik dit enkel voor testdata of duplicaten,
          niet voor echte panden.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button"
                  onClick={onCancel}
                  data-testid="hard-delete-cancel"
                  className="btn btn-ghost !h-9 !px-4 !text-[13px]">
            Annuleren
          </button>
          <button type="button"
                  disabled={busy}
                  onClick={onConfirm}
                  data-testid="hard-delete-confirm"
                  className="inline-flex items-center gap-1.5 h-9 px-4 text-[13px] font-semibold
                             rounded-md border-2 border-[#B43232] bg-[#B43232] text-white
                             hover:bg-[#9B2424] disabled:opacity-50">
            <Trash2 size={13} /> {busy ? "Bezig…" : "Definitief wissen"}
          </button>
        </div>
      </div>
    </div>
  );
}

export { REASONS };
